import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, CircleDashed, Download, FileJson, FileText, Loader, RefreshCw, Search, ShieldCheck, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { StaffHubHero, StaffHubNav } from '../components/staff/StaffHubNav';
import { StatTile } from '../components/ui/StatTile';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { ClickToReveal } from '../components/ClickToReveal';
import { getStaffPerms } from '../utils/staffPermissions';
import { DATA_CONTROLLER_PERMISSION, canAccessDataController } from '../utils/dataControllerPermissions';
import { runSubjectExport } from '../services/dataControllerExport';
import { isMinecraftUuid } from '../utils/dataControllerFormat';
import { PlayerSearchField, type PlayerSelection } from '../components/eventteam/PlayerSearchField';
import type { PdfBuildProgress } from '../components/datacontroller/pdfBuild';
import { ErasurePanel } from '../components/datacontroller/ErasurePanel';
import type { DataSourceCategory, SourceProgress, SubjectDataExport } from '../types/dataController';
import { cn } from '../utils/cn';
import logoUrl from '../../logo.png';

/**
 * Staff Manager · Data Controller — compile everything CDU holds about one player into a branded
 * PDF (and a complete JSON) for data-protection requests. Gated by `argus.datacontroller` (or
 * SPY_Super_admin) on both the client and every backend call.
 */

type Phase = 'idle' | 'resolving' | 'running' | 'done';

const CATEGORY_LABEL: Record<DataSourceCategory, string> = {
  identity: 'Identity & account',
  gameplay: 'Gameplay',
  economy: 'Economy',
  moderation: 'Moderation',
  logs: 'Activity logs',
  community: 'Community & events',
  staff: 'Staff records',
  system: 'System & audit',
};
const CATEGORY_ORDER: DataSourceCategory[] = ['identity', 'gameplay', 'economy', 'moderation', 'logs', 'community', 'staff', 'system'];

const STATE_META: Record<SourceProgress['state'], { label: string; badge: string; icon: typeof CheckCircle2 }> = {
  pending: { label: 'Queued', badge: 'badge-neutral', icon: CircleDashed },
  running: { label: 'Querying', badge: 'badge-accent', icon: Loader },
  ok: { label: 'Data held', badge: 'badge-success', icon: CheckCircle2 },
  empty: { label: 'No data', badge: 'badge-neutral', icon: CircleDashed },
  error: { label: 'Unavailable', badge: 'badge-danger', icon: XCircle },
  skipped: { label: 'Not queried', badge: 'badge-warning', icon: AlertCircle },
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

async function fetchDataUrl(url: string): Promise<string | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return await blobToDataUrl(await response.blob());
  } catch {
    return null;
  }
}

const avatarUrl = (uuid: string) => `${import.meta.env.DEV ? 'https://spy.playcdu.co' : ''}/cdu-heads/avatar/${uuid}/64.png`;

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

const slug = (value: string) => value.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

export const DataControllerView = () => {
  useDocumentTitle('Data Controller');
  const { identity } = useAuth();
  const isDev = import.meta.env.DEV;
  const perms = useMemo(() => getStaffPerms(identity, isDev), [identity, isDev]);
  const allowed = canAccessDataController(identity, isDev);

  // The same username-first player search the rest of Argus uses; a pasted UUID is accepted as-is.
  const [picked, setPicked] = useState<PlayerSelection | null>(null);
  const [discordHint, setDiscordHint] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [phase, setPhase] = useState<Phase>('idle');
  const [progress, setProgress] = useState<SourceProgress[]>([]);
  const [result, setResult] = useState<SubjectDataExport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState<'pdf' | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [buildProgress, setBuildProgress] = useState<PdfBuildProgress | null>(null);
  const runId = useRef(0);

  useEffect(() => {
    if (phase !== 'running') return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  const run = useCallback(async () => {
    if (!picked) return;
    const id = ++runId.current;
    setError(null);
    setBuildError(null);
    setResult(null);
    setProgress([]);
    setPhase('resolving');
    try {
      const uuid = picked.uuid.trim().toLowerCase(), username = picked.name?.trim() || null;
      if (!isMinecraftUuid(uuid)) throw new Error(`"${picked.uuid}" is not a Minecraft UUID.`);
      if (runId.current !== id) return;
      setPhase('running');
      const data = await runSubjectExport({
        uuid,
        viewer: identity,
        hints: {
          username,
          discordId: /^\d{17,20}$/.test(discordHint.trim()) ? discordHint.trim() : null,
          email: emailHint.trim().includes('@') ? emailHint.trim().toLowerCase() : null,
        },
        onProgress: (p) => {
          if (runId.current === id) setProgress(p);
        },
      });
      if (runId.current !== id) return;
      data.subject.avatarDataUrl = await fetchDataUrl(avatarUrl(data.subject.uuid));
      setResult(data);
      setPhase('done');
    } catch (e) {
      if (runId.current !== id) return;
      setError(e instanceof Error ? e.message : String(e));
      setPhase('idle');
    }
  }, [picked, discordHint, emailHint, identity]);

  const downloadPdf = useCallback(async () => {
    if (!result) return;
    setBuilding('pdf');
    setBuildError(null);
    try {
      const { buildSubjectPdf } = await import('../components/datacontroller/pdfBuild');
      const logoDataUrl = await fetchDataUrl(logoUrl);
      const blob = await buildSubjectPdf(result, logoDataUrl, setBuildProgress);
      triggerDownload(blob, `${result.reference}-${slug(result.subject.username ?? result.subject.uuid)}.pdf`);
    } catch (e) {
      setBuildError(e instanceof Error ? e.message : String(e));
    } finally {
      setBuilding(null);
      setBuildProgress(null);
    }
  }, [result]);

  const downloadJson = useCallback(() => {
    if (!result) return;
    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
    triggerDownload(blob, `${result.reference}-${slug(result.subject.username ?? result.subject.uuid)}.json`);
  }, [result]);

  const grouped = useMemo(() => {
    const byCategory = new Map<DataSourceCategory, SourceProgress[]>();
    for (const p of progress) {
      const list = byCategory.get(p.category) ?? [];
      list.push(p);
      byCategory.set(p.category, list);
    }
    return CATEGORY_ORDER.filter((c) => byCategory.has(c)).map((c) => ({ category: c, items: byCategory.get(c)! }));
  }, [progress]);

  const counts = useMemo(() => {
    const c = { total: progress.length, done: 0, ok: 0, error: 0, skipped: 0, records: 0 };
    for (const p of progress) {
      if (p.state === 'ok' || p.state === 'empty' || p.state === 'error' || p.state === 'skipped') c.done += 1;
      if (p.state === 'ok') c.ok += 1;
      if (p.state === 'error') c.error += 1;
      if (p.state === 'skipped') c.skipped += 1;
      c.records += p.recordCount ?? 0;
    }
    return c;
  }, [progress]);

  if (!allowed) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-glass-300">You don't have permission to use the Data Controller.</p>
        <p className="text-glass-500 text-sm mt-1">
          Ask an admin for the <code>{DATA_CONTROLLER_PERMISSION}</code> permission.
        </p>
      </div>
    );
  }

  const busy = phase === 'resolving' || phase === 'running';
  const subject = result?.subject;

  return (
    <div className="space-y-6">
      <StaffHubHero
        title="Data Controller"
        icon={ShieldCheck}
        blurb="Compile everything Craft Down Under holds about one player, from every system ARGUS reads, into a branded PDF for data-protection requests. Every export is recorded in the audit log."
        nav={<StaffHubNav perms={perms} />}
      />

      <div className="glass-card p-4 md:p-5">
        <SectionHeader title="Subject" icon={Search} description="Search the player by username, the same search as everywhere else in Argus, or paste a Minecraft UUID." />
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void run();
          }}
        >
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="relative flex-1">
              <PlayerSearchField value={picked} onChange={setPicked} placeholder="Search player by name, or paste a UUID…" autoFocus />
            </div>
            <button type="submit" className="btn-primary" disabled={busy || !picked}>
              {busy ? <Loader className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {phase === 'resolving' ? 'Resolving…' : phase === 'running' ? 'Compiling…' : 'Compile export'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Discord ID (optional)</label>
              <input className="glass-input w-full font-mono" placeholder="Auto-resolved from the account when known" value={discordHint} onChange={(e) => setDiscordHint(e.target.value)} disabled={busy} />
            </div>
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Email address (optional)</label>
              <input className="glass-input w-full" placeholder="Auto-resolved from Tesseract for staff accounts" value={emailHint} onChange={(e) => setEmailHint(e.target.value)} disabled={busy} />
              <p className="text-xs text-glass-500 mt-1">Only supply identifiers you have verified belong to the same person: they widen the match to appeals, watch lists and the audit trail.</p>
            </div>
          </div>
          {error && (
            <div className="alert alert-danger">
              <AlertCircle />
              {error}
            </div>
          )}
        </form>
      </div>

      {phase !== 'idle' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile label="Systems queried" value={counts.total} hint={busy ? `${counts.done} finished` : undefined} loading={counts.total === 0} />
          <StatTile label="Hold data" value={counts.ok} tone="success" loading={counts.total === 0} />
          <StatTile label="Records" value={counts.records.toLocaleString('en-AU')} tone="accent" loading={counts.total === 0} />
          <StatTile label="Unavailable" value={counts.error} tone={counts.error > 0 ? 'danger' : 'neutral'} hint={counts.skipped > 0 ? `${counts.skipped} not queried` : undefined} loading={counts.total === 0} />
        </div>
      )}

      {result && subject && (
        <div className="glass-card p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-4 min-w-0">
              {subject.avatarDataUrl ? (
                <img src={subject.avatarDataUrl} alt="" className="w-14 h-14 rounded-md ring-1 ring-glass-200/15 flex-shrink-0" />
              ) : (
                <span className="icon-tile icon-tile-lg icon-tile-accent">
                  <ShieldCheck className="w-5 h-5" />
                </span>
              )}
              <div className="min-w-0">
                <div className="eyebrow">Export ready · {result.reference}</div>
                <h2 className="text-lg font-semibold text-white truncate">{subject.username ?? 'Unknown player'}</h2>
                <div className="text-xs font-mono text-glass-400 break-all">{subject.uuid}</div>
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                  <div className="flex gap-2">
                    <dt className="text-glass-500 w-24 flex-shrink-0">Discord</dt>
                    <dd className="text-glass-300 font-mono">{subject.discordId ? `${subject.discordUsername ? `${subject.discordUsername} · ` : ''}${subject.discordId}` : '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-glass-500 w-24 flex-shrink-0">Email</dt>
                    <dd className="text-glass-300">{subject.email ?? '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-glass-500 w-24 flex-shrink-0">Tesseract</dt>
                    <dd className="text-glass-300 font-mono break-all">{subject.tesseractId ?? '—'}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="text-glass-500 w-24 flex-shrink-0">Also known as</dt>
                    <dd className="text-glass-300">{subject.aliases.length ? subject.aliases.join(', ') : '—'}</dd>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <dt className="text-glass-500 w-24 flex-shrink-0">IP addresses</dt>
                    <dd className="text-glass-300 flex flex-wrap gap-1.5">
                      {subject.knownIps.length === 0 && '—'}
                      {subject.knownIps.map((ip) => (
                        <ClickToReveal key={ip} value={ip} />
                      ))}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="btn-primary" onClick={() => void downloadPdf()} disabled={building !== null}>
                {building === 'pdf' ? <Loader className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {building === 'pdf' ? (buildProgress ? `Building PDF ${buildProgress.step} of ${buildProgress.total}` : 'Building PDF…') : 'Download PDF'}
              </button>
              <button type="button" className="glass-button" onClick={downloadJson}>
                <FileJson className="w-4 h-4" />
                Download JSON
              </button>
              <button type="button" className="glass-button-secondary" onClick={() => void run()}>
                <RefreshCw className="w-4 h-4" />
                Run again
              </button>
            </div>
          </div>
          {buildError && (
            <div className="alert alert-danger mt-4">
              <AlertCircle />
              The PDF could not be built: {buildError}
            </div>
          )}
          <div className="alert alert-warning mt-4">
            <AlertCircle />
            <span>
              This export contains personal data (identifiers, IP addresses, communications, moderation records). Handle it under the CDU privacy policy and delete working copies once the request is closed. Generated {format(new Date(result.generatedAt), 'PPpp')} by {result.generatedBy.email}; recorded in the audit log as <code>data_controller_export</code>.
            </span>
          </div>
        </div>
      )}

      {result && subject && phase === 'done' && <ErasurePanel subject={subject} />}

      {phase !== 'idle' && (
        <div className="glass-card p-4 md:p-5">
          <SectionHeader
            title="Systems"
            icon={Download}
            count={progress.length}
            description={busy ? 'Every system is queried with every identifier known for the player. Slow systems (chat, session streams) can take a minute.' : 'Every system queried, what it held, and anything that could not be reached.'}
          />
          {progress.length === 0 ? (
            <EmptyState bare compact icon={Loader} title="Resolving the player…" description="Looking the UUID up before fanning out." />
          ) : (
            <div className="space-y-4">
              {grouped.map(({ category, items }) => (
                <div key={category} className="subcard">
                  <div className="px-4 py-2.5 border-b border-glass-200/10 flex items-center justify-between">
                    <span className="eyebrow">{CATEGORY_LABEL[category]}</span>
                    <span className="text-xs text-glass-500 tabular-nums">{items.filter((i) => i.state === 'ok').length} of {items.length} hold data</span>
                  </div>
                  <ul className="divide-y divide-glass-200/10">
                    {items.map((item) => {
                      const meta = STATE_META[item.state];
                      const Icon = meta.icon;
                      return (
                        <li key={item.id} className="px-4 py-2.5 flex items-start gap-3">
                          <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', item.state === 'running' && 'animate-spin text-accent-300', item.state === 'ok' && 'text-green-400', item.state === 'error' && 'text-red-400', item.state === 'skipped' && 'text-yellow-400', (item.state === 'pending' || item.state === 'empty') && 'text-glass-500')} />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className="text-sm text-white">{item.title}</span>
                              <span className="text-xs text-glass-500">{item.system}</span>
                            </div>
                            {item.error && <div className="text-xs text-glass-400 mt-0.5 break-words">{item.error}</div>}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {item.state === 'ok' && <span className="text-xs text-glass-400 tabular-nums">{(item.recordCount ?? 0).toLocaleString('en-AU')} rec.</span>}
                            {typeof item.durationMs === 'number' && <span className="text-xs text-glass-500 tabular-nums hidden md:inline">{(item.durationMs / 1000).toFixed(1)} s</span>}
                            <span className={cn('badge', meta.badge)}>{meta.label}</span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {phase === 'idle' && !error && (
        <EmptyState
          icon={ShieldCheck}
          title="Nothing compiled yet"
          description="Enter a player's UUID above. ARGUS will query the Users API, Redis, Elasticsearch, Tesseract, the steward, IdentAware and every ARGUS database table, then produce the report."
        />
      )}
    </div>
  );
};
